import axios from 'axios';

const M = '/api/method';
const base = (import.meta.env.VITE_API_BASE_URL && import.meta.env.VITE_API_BASE_URL.trim()) || window.location.origin;

// Create axios instance with base configuration
const api = axios.create({
  baseURL: base,
  timeout: 30000,
});

// Add request interceptor to include auth token if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token && config.headers) {
      config.headers['X-ESIM-Admin-Auth'] = `Bearer ${token}`;
    }
    // JSON body for Frappe is fine
    if (config.method !== 'get' && config.headers) {
      config.headers['Content-Type'] = 'application/json';
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle Frappe API response format and error normalization
api.interceptors.response.use(
  (response) => {
    // Frappe API responses are wrapped in a 'message' object
    if (response.data && response.data.message) {
      response.data = response.data.message;
    }
    return response;
  },
  (error) => {
    const status = error?.response?.status;
    // Handle auth failures consistently
    if (status === 401 || status === 403 || status === 417) {
      try {
        sessionStorage.setItem('auth_expired', '1');
        try {
          const currentPath = window.location.pathname + window.location.search + window.location.hash;
          sessionStorage.setItem('auth_expired_path', currentPath);
        } catch {}
      } catch {}

      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.dispatchEvent(new CustomEvent('auth-expired'));
      return Promise.reject(new Error('Authentication expired. Please log in again.'));
    }
    
    if (error?.response?.data?.exception || error?.response?.data?._error_message) {
      const msg = error.response.data._error_message || error.response.data.exception;
      return Promise.reject(new Error(msg));
    }
    return Promise.reject(error);
  }
);

export interface Partner {
  name: string;
  code: string;
}

export interface DriverUser {
  name: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  status: 'ACTIVE' | 'ARCHIVED';
  source: 'MANUAL' | 'CSV' | 'API';
  created_at: string;
  archived_at?: string;
  external_driver_id?: string;
  customer?: string;
  partner?: Partner | null;
  // Subscription data from ESIM Driver Profile
  item_code?: string;
  due_date?: string;
  renewal_count?: number;
  profile_status?: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED' | 'PENDING' | 'FAILED';
  iccid?: string;
  activation_date?: string;
  esim_profile?: {
    profile?: {
      status?: string;
      [key: string]: any;
    };
    usage?: {
      status?: string;
      data_used_mb?: number;
      data_remaining_mb?: number;
      data_total_mb?: number;
      expired_at?: string;
      is_unlimited?: boolean;
    };
  };
}

export interface DriverProfile {
  name: string;
  driver_user: string;
  iccid: string;
  item_code: string;
  status: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED' | 'PENDING' | 'FAILED';
  due_date?: string;
  renewal_count: number;
  renewal_cap: number;
  last_issued_at?: string;
  last_email_sent_at?: string;
  last_failure_reason?: string;
  is_auto_renew?: boolean;
  pdf_file?: {
    file_name: string;
    file_url: string;
    file_size: number;
    creation: string;
    download_url: string;
  };
  profile_link?: string;
  activation_date?: string;
  esim_profile?: {
    profile?: {
      status?: string;
      [key: string]: any;
    };
    usage?: {
      status?: string;
      data_used_mb?: number;
      data_remaining_mb?: number;
      data_total_mb?: number;
      expired_at?: string;
      is_unlimited?: boolean;
    };
  };
}

export interface DriverEvent {
  name: string;
  ts: string;
  driver_user?: string;
  driver_profile?: string;
  event_type: string;
  actor?: string;
  data?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
}

export interface DriverUsersResponse {
  data: DriverUser[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
  };
}

export interface DriverEventsResponse {
  data: DriverEvent[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
  };
}

export interface CreateUserRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
}

export interface ImportUsersRequest {
  csvData: string;
}

export interface IssueEsimRequest {
  user_name: string;
  item_code: string;
}

export interface SetProfileStatusRequest {
  profile_name: string;
  status: string;
}

export interface SetDueDateRequest {
  profile_name: string;
  due_date: string;
}

export const driverApi = {
  // User Management
  async getUsers(params: {
    page?: number;
    page_size?: number;
    status?: string;
    search?: string;
    email?: string;
    name?: string;
    partner?: string;
    hasActiveEsim?: string;
    nextDue?: string;
  } = {}): Promise<DriverUsersResponse> {
    const filters: any = {};
    if (params.status) filters.status = params.status;
    if (params.search) filters.search = params.search;
    if (params.email) filters.email = params.email;
    if (params.name) filters.name = params.name;
    if (params.partner) filters.partner = params.partner;
    if (params.hasActiveEsim) filters.hasActiveEsim = params.hasActiveEsim;
    if (params.nextDue) filters.nextDue = params.nextDue;

    const queryParams = new URLSearchParams();
    if (Object.keys(filters).length > 0) {
      queryParams.append('filters_json', JSON.stringify(filters));
    }
    queryParams.append('page', (params.page || 1).toString());
    queryParams.append('page_size', (params.page_size || 50).toString());
    
    // Use AND mode if search contains 2 or more words, otherwise use OR
    const searchWords = params.search?.trim().split(/\s+/).filter(word => word.length > 0) || [];
    const searchMode = searchWords.length >= 2 ? 'AND' : 'OR';
    queryParams.append('search_mode', searchMode);

    const response = await api.get(`${M}/ski_delivery.api.esim_admin.driver_list_users?${queryParams.toString()}`);
    return response.data;
  },

  async getUser(userName: string): Promise<{ user: DriverUser; profiles: DriverProfile[]; events: DriverEvent[] }> {
    const response = await api.get(`${M}/ski_delivery.api.esim_admin.driver_get_user?user_name=${userName}`);
    return response.data;
  },

  async createUser(userData: CreateUserRequest): Promise<DriverUser> {
    const response = await api.post(`${M}/ski_delivery.api.esim_admin.driver_create_user`, userData);
    return response.data;
  },

  async importUsers(csvData: string): Promise<{ created: number; skipped_existing: number }> {
    // Convert CSV data to base64 as expected by the backend
    const base64Data = btoa(csvData);
    const response = await api.post(`${M}/ski_delivery.api.esim_admin.driver_import_users_csv`, { file_b64: base64Data });
    return response.data;
  },

  async archiveUser(userName: string): Promise<void> {
    await api.post(`${M}/ski_delivery.api.esim_admin.driver_archive_user`, { name: userName });
  },

  async unarchiveUser(userName: string): Promise<void> {
    await api.post(`${M}/ski_delivery.api.esim_admin.driver_unarchive_user`, { name: userName });
  },

  async issueEsim(request: IssueEsimRequest): Promise<DriverProfile> {
    const response = await api.post(`${M}/ski_delivery.api.esim_admin.driver_issue_esim`, request);
    return response.data;
  },

  async setProfileStatus(request: SetProfileStatusRequest): Promise<void> {
    await api.post(`${M}/ski_delivery.api.esim_admin.driver_set_profile_status`, request);
  },


  // Audit & History
  async getEvents(params: {
    page?: number;
    page_size?: number;
    driver_user?: string;
    event_type?: string;
    start_date?: string;
    end_date?: string;
    status?: string;
    search?: string;
  } = {}): Promise<DriverEventsResponse> {
    const filters: any = {};
    if (params.driver_user) filters.driver_user = params.driver_user;
    if (params.event_type) filters.event_type = params.event_type;
    if (params.start_date) filters.start_date = params.start_date;
    if (params.end_date) filters.end_date = params.end_date;
    if (params.status) filters.status = params.status;
    if (params.search) filters.search = params.search;

    const queryParams = new URLSearchParams();
    if (Object.keys(filters).length > 0) {
      queryParams.append('filters_json', JSON.stringify(filters));
    }
    queryParams.append('page', (params.page || 1).toString());
    queryParams.append('page_size', (params.page_size || 50).toString());
    queryParams.append('search_mode', 'OR');

    const response = await api.get(`${M}/ski_delivery.api.esim_admin.driver_audit_events?${queryParams.toString()}`);
    return response.data;
  },

  // Background Jobs
  async getJobs(params: {
    page?: number;
    page_size?: number;
    status?: string;
    search?: string;
  } = {}): Promise<{ data: any[]; pagination: any }> {
    const filters: any = {};
    if (params.status) filters.status = params.status;
    if (params.search) filters.search = params.search;

    const queryParams = new URLSearchParams();
    if (Object.keys(filters).length > 0) {
      queryParams.append('filters_json', JSON.stringify(filters));
    }
    queryParams.append('page', (params.page || 1).toString());
    queryParams.append('page_size', (params.page_size || 50).toString());
    queryParams.append('search_mode', 'OR');

    const response = await api.get(`${M}/ski_delivery.api.esim_admin.driver_background_jobs?${queryParams.toString()}`);
    return response.data;
  },

  async getJobDetails(jobId: string): Promise<any> {
    const response = await api.get(`${M}/ski_delivery.api.esim_admin.driver_job_details?job_id=${jobId}`);
    return response.data;
  },


  async getPdf(profileName: string): Promise<{ file_name: string; file_url: string; file_size: number; creation: string; download_url: string }> {
    const response = await api.get(`${M}/ski_delivery.api.esim_admin.driver_get_pdf`, {
      params: { profile_name: profileName }
    });
    return response.data;
  },

  async downloadPdf(fileName: string): Promise<{ file_name: string; content: string; content_type: string }> {
    const response = await api.get(`${M}/ski_delivery.api.esim_admin.download_pdf`, {
      params: { file_name: fileName }
    });
    return response.data;
  },

  async resendEmail(profileName: string): Promise<{ success: boolean; message: string; email: string }> {
    const response = await api.post(`${M}/ski_delivery.api.esim_admin.resend_email`, {
      profile_name: profileName
    });
    return response.data;
  },

  async getProducts(region?: string, validityDays?: number, dataMb?: number): Promise<{ success: boolean; products: any[] }> {
    const params = new URLSearchParams();
    if (region) {
      params.append('region', region);
    }
    if (validityDays !== undefined) {
      params.append('validity_days', validityDays.toString());
    }
    if (dataMb !== undefined) {
      params.append('data_mb', dataMb.toString());
    }
    const queryString = params.toString();
    const url = `${M}/ski_delivery.api.esim.list_products${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);
    return response.data;
  },

  // Get top-up packages for a specific ICCID
  async getTopupPackages(iccid: string): Promise<{ success: boolean; packages: any[] }> {
    const response = await api.get(`${M}/ski_delivery.api.esim_admin.get_topup_packages?iccid=${iccid}`);
    return response.data;
  },

  // Perform top-up for a specific ICCID
  async topup(iccid: string, itemCode: string, qty: number = 1): Promise<{ status: string; result: any; provider_topup_id: string }> {
    const response = await api.post(`${M}/ski_delivery.api.esim_admin.topup`, {
      iccid,
      item_code: itemCode,
      qty
    });
    return response.data;
  },

  // Set due date for a driver profile
  async setDueDate(profileName: string, dueDate: string): Promise<{ ok: boolean; message: string }> {
    const response = await api.post(`${M}/ski_delivery.api.esim_admin.driver_set_due_date`, {
      profile_name: profileName,
      due_date: dueDate
    });
    return response.data;
  },

  // Set renewal top-up product for a driver profile
  async setRenewalTopupPackage(profileName: string, renewalTopupProductId: string | null): Promise<{ ok: boolean; message: string }> {
    const response = await api.post(`${M}/ski_delivery.api.esim_admin.driver_set_renewal_topup_package`, {
      profile_name: profileName,
      renewal_topup_product_id: renewalTopupProductId
    });
    return response.data;
  },

  // Get renewal top-up plan details for a driver profile
  async getRenewalTopupPlan(profileName: string): Promise<{ ok: boolean; plan: any; message?: string; error?: string }> {
    const response = await api.get(`${M}/ski_delivery.api.esim_admin.driver_get_renewal_topup_plan`, {
      params: { profile_name: profileName }
    });
    return response.data;
  },

  // Update auto-renew setting for a driver profile
  async updateAutoRenew(profileName: string, isAutoRenew: boolean): Promise<{ ok: boolean; message: string }> {
    const response = await api.post(`${M}/ski_delivery.api.esim_admin.driver_update_auto_renew`, {
      profile_name: profileName,
      is_auto_renew: isAutoRenew
    });
    return response.data;
  },

  // Get list of unique partner names
  async getPartners(): Promise<{ partners: string[] }> {
    const response = await api.get(`${M}/ski_delivery.api.esim_admin.driver_get_partners`);
    return response.data;
  }
};
